import { useEffect, useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi } from '@/hooks/useApi';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import {
  MARCA_LABEL,
  TIPO_LABEL,
  TIPO_MARCA,
  TIPOS,
  type KitAjuste,
  type MinimoAjuste,
  type ModeloAjuste,
  type TipoDispositivo,
} from '@/lib/dispositivos';
import { olvidarCatalogosDispositivos } from './catalogos';

/**
 * Los ajustes de Dispositivos que no son una lista de id + nombre: los
 * modelos (con su tipo y si llevan Wi-Fi), los mínimos de stock por tipo y el
 * kit de una casa nueva. Los tipos de incidencia sí son id + nombre y usan
 * el editor genérico de Configuración.
 */

interface Setting<T> {
  id: string;
  values: T[];
}

export function AjusteModelos() {
  const { data, loading, refresh } = useApi<Setting<ModeloAjuste>>(() => api.getSetting('dispositivos_modelos'), []);
  const [nuevo, setNuevo] = useState({ label: '', tipo: 'smartlock' as TipoDispositivo, wifiIntegrado: false });
  const [guardando, setGuardando] = useState(false);

  if (loading || !data) return <LoadingSpinner />;

  const guardar = async (values: ModeloAjuste[]) => {
    setGuardando(true);
    try {
      await api.updateSetting('dispositivos_modelos', { values });
      olvidarCatalogosDispositivos();
      refresh();
    } finally {
      setGuardando(false);
    }
  };

  const anadir = async () => {
    const label = nuevo.label.trim();
    if (!label) return;
    const id = label.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
    if (data.values.some((m) => m.id === id)) return alert('Ya hay un modelo con ese nombre.');
    await guardar([...data.values, { id, label, tipo: nuevo.tipo, marca: TIPO_MARCA[nuevo.tipo], wifiIntegrado: nuevo.tipo === 'smartlock' && nuevo.wifiIntegrado }]);
    setNuevo({ label: '', tipo: nuevo.tipo, wifiIntegrado: false });
  };

  const alternarWifi = (m: ModeloAjuste) => guardar(data.values.map((x) => (x.id === m.id ? { ...x, wifiIntegrado: !x.wifiIntegrado } : x)));
  const quitar = (m: ModeloAjuste) => {
    if (!confirm(`¿Quitar el modelo «${m.label}»? Los dispositivos que lo tengan quedarán «por confirmar».`)) return;
    void guardar(data.values.filter((x) => x.id !== m.id));
  };

  const porTipo = new Map<TipoDispositivo, ModeloAjuste[]>();
  for (const m of data.values) porTipo.set(m.tipo, [...(porTipo.get(m.tipo) || []), m]);

  return (
    <div className="card">
      <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-1">Modelos</h3>
      <p className="text-xs text-sereno-dark/50 mb-4">
        Lo único que decide un modelo es si la smartlock lleva Wi-Fi integrado (las Pro): esas no necesitan bridge.
      </p>
      <div className="space-y-4 mb-4">
        {TIPOS.filter((t) => porTipo.has(t)).map((t) => (
          <div key={t}>
            <p className="text-xs font-medium text-sereno-dark/50 mb-1">{MARCA_LABEL[TIPO_MARCA[t]]} · {TIPO_LABEL[t]}</p>
            <div className="space-y-1">
              {(porTipo.get(t) || []).map((m) => (
                <div key={m.id} className="flex items-center gap-3 p-2 rounded-lg border border-gray-100">
                  <span className="text-xs text-sereno-dark/40 font-mono w-40 truncate flex-shrink-0">{m.id}</span>
                  <span className="flex-1 text-sm min-w-0 truncate">{m.label}</span>
                  {m.tipo === 'smartlock' && (
                    <label className="flex items-center gap-1.5 text-xs text-sereno-dark/60">
                      <input type="checkbox" checked={m.wifiIntegrado} disabled={guardando} onChange={() => void alternarWifi(m)} />
                      Wi-Fi
                    </label>
                  )}
                  <button onClick={() => quitar(m)} className="text-red-300 hover:text-red-500"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <select className="input sm:w-56" value={nuevo.tipo} onChange={(e) => setNuevo((n) => ({ ...n, tipo: e.target.value as TipoDispositivo }))}>
          {TIPOS.map((t) => (
            <option key={t} value={t}>{MARCA_LABEL[TIPO_MARCA[t]]} · {TIPO_LABEL[t]}</option>
          ))}
        </select>
        <input className="input sm:flex-1" placeholder="Nombre del modelo" value={nuevo.label} onChange={(e) => setNuevo((n) => ({ ...n, label: e.target.value }))} />
        {nuevo.tipo === 'smartlock' && (
          <label className="flex items-center gap-1.5 text-xs text-sereno-dark/60 whitespace-nowrap">
            <input type="checkbox" checked={nuevo.wifiIntegrado} onChange={(e) => setNuevo((n) => ({ ...n, wifiIntegrado: e.target.checked }))} />
            Wi-Fi integrado
          </label>
        )}
        <button onClick={() => void anadir()} disabled={guardando} className="btn-primary flex items-center gap-1"><Plus size={14} /> Añadir</button>
      </div>
    </div>
  );
}

function EditorCantidades<T extends { id: TipoDispositivo; label: string }>({
  settingId,
  titulo,
  descripcion,
  campo,
}: {
  settingId: string;
  titulo: string;
  descripcion: string;
  campo: 'minimo' | 'cantidad';
}) {
  const { data, loading, refresh } = useApi<Setting<T & Record<'minimo' | 'cantidad', number>>>(() => api.getSetting(settingId), [settingId]);
  const [valores, setValores] = useState<Record<string, string>>({});
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    if (data) setValores(Object.fromEntries(data.values.map((v) => [v.id, String(v[campo] ?? 0)])));
  }, [data, campo]);

  if (loading || !data) return <LoadingSpinner />;

  const conocidos = new Set(data.values.map((v) => v.id));
  const faltan = TIPOS.filter((t) => !conocidos.has(t));

  const guardar = async () => {
    setGuardando(true);
    try {
      const values = [
        ...data.values.map((v) => ({ ...v, [campo]: Math.max(0, Number(valores[v.id] ?? 0) || 0) })),
        ...faltan.filter((t) => valores[t] !== undefined && valores[t] !== '').map((t) => ({ id: t, label: TIPO_LABEL[t], [campo]: Math.max(0, Number(valores[t]) || 0) })),
      ];
      await api.updateSetting(settingId, { values });
      olvidarCatalogosDispositivos();
      refresh();
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="card">
      <h3 className="text-sm font-semibold text-sereno-dark/60 uppercase tracking-wider mb-1">{titulo}</h3>
      <p className="text-xs text-sereno-dark/50 mb-4">{descripcion}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-4">
        {[...data.values.map((v) => ({ id: v.id, label: v.label })), ...faltan.map((t) => ({ id: t, label: TIPO_LABEL[t] }))].map((v) => (
          <label key={v.id} className="block">
            <span className="text-xs text-sereno-dark/60">{v.label}</span>
            <input
              type="number"
              min={0}
              className="input mt-1"
              value={valores[v.id] ?? ''}
              onChange={(e) => setValores((prev) => ({ ...prev, [v.id]: e.target.value }))}
            />
          </label>
        ))}
      </div>
      <button onClick={() => void guardar()} disabled={guardando} className="btn-primary">{guardando ? 'Guardando…' : 'Guardar'}</button>
    </div>
  );
}

export function AjusteMinimos() {
  return (
    <EditorCantidades<MinimoAjuste>
      settingId="dispositivos_minimos"
      titulo="Mínimos de stock"
      descripcion="Por debajo de esto, la pestaña Stock lo marca en rojo y el menú lo cuenta."
      campo="minimo"
    />
  );
}

export function AjusteKit() {
  return (
    <EditorCantidades<KitAjuste>
      settingId="dispositivos_kit"
      titulo="Kit de casa nueva"
      descripcion="Lo que hace falta para montar una casa. El opener va a cero: se añade si hay portal."
      campo="cantidad"
    />
  );
}
